import {
  Box as Flex,
  Chip as Tag,
  Typography as Text,
} from '@material-ui/core';

import {
  LabelUtils,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';
import { RuleFragment, getTypeOrNull } from '@spotify/confidence-graphql';
import { getRuleTypeDisplayName } from '@spotify/plugin-confidence-assignment-rules-react';
import { formatRuleId } from '@spotify/plugin-confidence-flags-react';

import styles from './RuleItem.module.css';

const getRuleVariantNames = (rule: RuleFragment) =>
  (rule.assignmentSpec?.assignments ?? [])
    .map(assignment => getTypeOrNull(assignment.variant?.variant)?.name)
    .filter((name): name is string => Boolean(name))
    .map(name => extractLastNameComponent(name) ?? name);

interface RuleItemProps {
  rule: RuleFragment;
}

export const RuleItem = ({ rule }: RuleItemProps) => {
  const id = formatRuleId(rule.name);
  const ruleTypeRaw = LabelUtils.getLabelValue(rule.labels ?? [], 'ruleType');
  const ruleType =
    getRuleTypeDisplayName(ruleTypeRaw) || 'Conditional targeting';
  const variantNames = getRuleVariantNames(rule);
  const targetingKey = rule.targetingKeySelector;

  return (
    <Flex
      display="flex"
      flexDirection="column"
      className={styles.ruleItem}
      style={{
        gap: 'var(--encore-spacing-tighter-4)',
      }}
    >
      <Flex
        display="flex"
        alignItems="center"
        style={{
          gap: 'var(--encore-spacing-tighter-2)',
        }}
      >
        <Text
          variant="body2"
          className={styles.ruleId}
          style={{
            fontWeight: 'bold',
          }}
        >
          {id}
        </Text>
        <Text variant="body2" color="textSecondary">
          {ruleType}
        </Text>
        {rule.enabled === false && (
          <Text variant="body2" color="textSecondary">
            (disabled)
          </Text>
        )}
      </Flex>
      {rule.description && <Text variant="body2">{rule.description}</Text>}
      {targetingKey && (
        <Flex
          display="flex"
          alignItems="center"
          style={{
            gap: 'var(--encore-spacing-tighter-2)',
          }}
        >
          <Text variant="body2" color="textSecondary">
            Randomized by
          </Text>
          <Flex
            display="flex"
            flexWrap="wrap"
            style={{
              gap: 'var(--encore-spacing-tighter-2)',
            }}
            aria-label="Randomization key"
          >
            <Tag id={targetingKey} label={targetingKey} size="small" />
          </Flex>
        </Flex>
      )}
      {variantNames.length > 0 && (
        <Text variant="body2" color="textSecondary">
          {`Assigns: ${variantNames.join(', ')}`}
        </Text>
      )}
      {(() => {
        const environments = (rule.environments ?? [])
          .map(env => getTypeOrNull(env))
          .filter((env): env is NonNullable<typeof env> => Boolean(env));
        if (environments.length === 0) return null;
        return (
          <Flex
            display="flex"
            alignItems="center"
            style={{
              gap: 'var(--encore-spacing-tighter-2)',
            }}
          >
            <Text variant="body2" color="textSecondary">
              Environments:
            </Text>
            <Flex
              display="flex"
              flexWrap="wrap"
              style={{
                gap: 'var(--encore-spacing-tighter-2)',
              }}
              aria-label="Environments"
            >
              {environments.map(env => {
                const envLabel = extractLastNameComponent(env.name) ?? env.name;
                return (
                  <Tag
                    key={env.name}
                    id={env.name}
                    label={envLabel}
                    size="small"
                  />
                );
              })}
            </Flex>
          </Flex>
        );
      })()}
    </Flex>
  );
};
